import {
  Component,
  Inject,
  NgZone,
  PLATFORM_ID,
  Input,
  OnDestroy,
  OnChanges,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

// interfaces imports
import { Chart } from '@/core/interfaces';

// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import {
  CourseCatalogAttributes,
  CourseInfoWithGradeRanges,
} from '../../../../../server/types';
import { DataService, ScreenSizeService } from '@/shared/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-prerequisite-chart',
  templateUrl: './prerequisite-chart.component.html',
  styleUrls: ['./prerequisite-chart.component.css'],
  standalone: false,
})
export class PrerequisiteChartComponent implements OnDestroy, OnChanges {
  @Input() displayData!: CourseInfoWithGradeRanges;
  @Input() targetCrs!: CourseCatalogAttributes;
  @Input() showLabels: boolean = true;
  @Input() targetCrsPassRate?: number;

  public screenWidth!: number;

  private root!: am5.Root;

  // chart variable
  private chart!: am5xy.XYChart;

  // data series variable
  private series1!: am5xy.ColumnSeries; //students that passed
  private series2!: am5xy.ColumnSeries; //students that failed

  // label variable
  private labelSubtitle!: am5.Label;
  private labelTitle!: am5.Label;
  private xAxisLabel!: am5.Label;

  // Define variable for chart data
  private chartData!: Chart[];
  private subscription = new Subscription();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private screenSizeService: ScreenSizeService,
    private dataService: DataService,
  ) {}

  ngOnInit() {
    this.subscription.add(
      this.screenSizeService.screenSize$.subscribe((size) => {
        this.screenWidth = size;
      }),
    );
    this.root = am5.Root.new('chartdiv');

    // Remove amcharts logo below graphs
    this.root._logo!.dispose();

    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panY: false,
        layout: this.root.verticalLayout,
      }),
    );

    //Define data
    this.chartData = [
      {
        category: 'A range',
        value1: this.displayData.a_range.num_passed,
        value2: this.displayData.a_range.num_failed,
      },
      {
        category: 'B range',
        value1: this.displayData.b_range.num_passed,
        value2: this.displayData.b_range.num_failed,
      },
      {
        category: 'C range',
        value1: this.displayData.c_range.num_passed,
        value2: this.displayData.c_range.num_failed,
      },
    ];
  }

  ngOnChanges() {
    // if there is data in series1 and series2
    // then update data for display with new data
    if (this.series1 && this.series2) {
      this.setNumPassedBar();
      this.setNumFailedBar();
    }
  }

  ngAfterViewInit() {
    // Chart code goes in here
    this.browserOnly(() => {
      this.root.setThemes([am5themes_Animated.new(this.root)]);

      // Create Y-axis
      let yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(this.root, {
          renderer: am5xy.AxisRendererY.new(this.root, {}),
        }),
      );

      // Create X-Axis
      let xAxis = this.chart.xAxes.push(
        am5xy.CategoryAxis.new(this.root, {
          renderer: am5xy.AxisRendererX.new(this.root, {}),
          categoryField: 'category',
        }),
      );
      xAxis.data.setAll(this.chartData);

      // Create series 1
      this.series1 = this.chart.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Passed',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value1',
          categoryXField: 'category',
        }),
      );
      this.series1.data.setAll(this.chartData);
      this.setNumPassedBar();

      // Create series 2
      this.series2 = this.chart.series.push(
        am5xy.ColumnSeries.new(this.root, {
          name: 'Failed',
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: 'value2',
          categoryXField: 'category',
        }),
      );
      this.series2.data.setAll(this.chartData);
      this.setNumFailedBar();

      const rootCopy = this.root;
      // Display data value labels for series 1
      this.series1.bullets.push(() => {
        return am5.Bullet.new(rootCopy, {
          locationY: 0.5,
          sprite: am5.Label.new(rootCopy, {
            text: this.showLabels
              ? 'Chance to pass: {pass_rate}%\nTotal: {value1}'
              : '',
            fill: am5.color(0x000),
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true,
            shadowColor: am5.color(0xffffff),
            shadowBlur: 5,
            shadowOffsetX: 2,
            shadowOffsetY: 2,
          }),
        });
      });

      // Display data value labels for series 2
      this.series2.bullets.push(() => {
        return am5.Bullet.new(rootCopy, {
          locationY: 0.5,
          sprite: am5.Label.new(rootCopy, {
            text: this.showLabels
              ? 'Chance to fail: {pass_rate}%\nTotal: {value2}'
              : '',
            fill: am5.color(0x000),
            centerY: am5.p50,
            centerX: am5.p50,
            populateText: true,
            shadowColor: am5.color(0xffffff),
            shadowBlur: 3,
            shadowOffsetX: 1,
            shadowOffsetY: 1,
          }),
        });
      });

      // Adding legend with custom settings based on screen size
      // if (this.screenWidth <= 599) {
      //   let legend = this.chart.children.push(
      //     am5.Legend.new(this.root, {
      //       y: am5.percent(150),
      //       centerY: am5.percent(150),
      //     }),
      //   );
      //   legend.data.setAll(this.chart.series.values);
      // } else if (this.screenWidth >= 600 && this.screenWidth <= 1024) {
      //   let legend = this.chart.children.push(
      //     am5.Legend.new(this.root, {
      //       y: am5.percent(150),
      //       centerY: am5.percent(150),
      //     }),
      //   );
      //   legend.data.setAll(this.chart.series.values);
      // } else {
      //   let legend = this.chart.children.push(am5.Legend.new(this.root, {}));
      //   legend.data.setAll(this.chart.series.values);
      // }

      // Add cursor
      this.chart.set('cursor', am5xy.XYCursor.new(this.root, {}));

      // Set colors for bar graph
      this.series1.columns.template.adapters.add('fill', (fill, target) => {
        if (target.dataItem) {
          return am5.color('rgba(0, 156, 222, 1)');
        }
        return fill;
      });

      this.series1.columns.template.setAll({
        fillOpacity: 1, // Ensure opacity from rgba is applied
        strokeOpacity: 1, // Optional: ensure stroke respects rgba transparency
      });

      this.series2.columns.template.adapters.add('fill', (fill, target) => {
        if (target.dataItem) {
          return am5.color('rgba(255, 99, 132, 1)');
        }
        return fill;
      });

      this.series2.columns.template.adapters.add('stroke', (stroke, target) => {
        if (target.dataItem) {
          return am5.color('rgba(255, 99, 132, 1)');
        }
        return stroke;
      });

      this.series2.columns.template.setAll({
        fillOpacity: 1, // Ensure opacity from rgba is applied
        strokeOpacity: 1, // Optional: ensure stroke respects rgba transparency
      });

      //Set tooltip data
      this.series1.columns.template.setAll({
        tooltipText:
          'Chance to pass ' +
          this.targetCrs.course_code +
          ': {pass_rate}%, {categoryX}: {valueY}',
        width: am5.percent(70),
        tooltipY: 0,
      });

      this.series2.columns.template.setAll({
        tooltipText:
          'Chance to fail ' +
          this.targetCrs.course_code +
          ': {pass_rate}%, {categoryX}: {valueY}',
        width: am5.percent(90),
        tooltipY: 0,
      });

      // Add label in Y-axis
      yAxis.axisHeader.children.push(
        am5.Label.new(this.root, {
          text: 'Enrolled (Count)',
          fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
          fontSize: 20,
          fontWeight: '500',
        }),
      );

      // Add titles
      // Title Label
      this.labelTitle = this.chart.children.unshift(
        am5.Label.new(this.root, {
          text: '',
          fontSize: 25,
          fontFamily: "'Arial', 'Helvetica', 'sans-serif'",
          fontWeight: '500',
          textAlign: 'center',
          x: am5.percent(50),
          y: am5.percent(0),
        }),
      );

      // Subtitle Label
      this.labelSubtitle = this.chart.children.unshift(
        am5.Label.new(this.root, {
          fontSize: 14,
          fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
          textAlign: 'center',
          x: am5.percent(50),
          y: am5.percent(6),
        }),
      );

      // Adding label in X-axis with custom settings based on screen size
      if (this.screenWidth <= 599) {
        this.xAxisLabel = this.chart.children.unshift(
          am5.Label.new(this.root, {
            fontSize: 18,
            fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
            fontWeight: '500',
            textAlign: 'center',
            oversizedBehavior: 'wrap',
            maxWidth: 250,
            x: am5.percent(50),
            centerX: am5.percent(50),
            y: am5.percent(100),
            centerY: am5.percent(100),
            paddingTop: 0,
            paddingBottom: 0,
            dy: 15,
          }),
        );
      } else {
        this.xAxisLabel = this.chart.children.unshift(
          am5.Label.new(this.root, {
            fontSize: this.showLabels ? 18 : 15,
            fontFamily: "'Gotham SSm A','Gotham SSm B', 'Arial', 'sans-serif'",
            fontWeight: '500',
            textAlign: 'center',
            x: am5.percent(50),
            centerX: am5.percent(50),
            y: am5.percent(100),
            centerY: am5.percent(100),
            marginTop: 50,
            paddingBottom: 0,
            dy: 15,
          }),
        );
      }

      // Changing label contents
      this.labelSubtitle.set('text', this.targetCrs.course_name);
      this.labelTitle.set(
        'text',
        `Target Course: ${this.targetCrs.course_code}${this.targetCrsPassRate !== undefined ? ' | Pass Rate: ' + this.targetCrsPassRate : ''}`,
      );
      this.xAxisLabel.set(
        'text',
        'Awarded Grades in ' +
          this.displayData.course_name +
          ' (' +
          this.displayData.course_code +
          ')',
      );
    });

    const legend = this.chart.children.push(
      am5.Legend.new(this.root, {
        nameField: 'name',
        fillField: 'color',
        strokeField: 'color',
      }),
    );

    legend.data.setAll([
      {
        name: 'Passed',
        color: am5.color('rgba(0, 156, 222, 1)'),
      },
      {
        name: 'Failed',
        color: am5.color('rgba(255, 99, 132, 1)'),
      },
    ]);
  }

  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
    this.subscription.unsubscribe();
  }

  // Set data for NumPassedBar ==> Series 1 (Dark Blue Bar)
  setNumPassedBar() {
    this.series1.data.setIndex(0, {
      category: 'A range',
      value1: this.displayData.a_range.num_passed,
      pass_rate: this.displayData.a_range.pass_rate,
      course_code: this.displayData.course_code,
    });
    this.series1.data.setIndex(1, {
      category: 'B range',
      value1: this.displayData.b_range.num_passed,
      pass_rate: this.displayData.b_range.pass_rate,
      course_code: this.displayData.course_code,
    });
    this.series1.data.setIndex(2, {
      category: 'C range',
      value1: this.displayData.c_range.num_passed,
      pass_rate: this.displayData.c_range.pass_rate,
      course_code: this.displayData.course_code,
    });
  }

  // Set data for NumFailedBar ==> Series 2 (Red Bar)
  setNumFailedBar() {
    this.series2.data.setIndex(0, {
      category: 'A range',
      value2: this.displayData.a_range.num_failed,
      pass_rate: this.displayData.a_range.fail_rate,
      course_code: this.displayData.course_code,
    });
    this.series2.data.setIndex(1, {
      category: 'B range',
      value2: this.displayData.b_range.num_failed,
      pass_rate: this.displayData.b_range.fail_rate,
      course_code: this.displayData.course_code,
    });
    this.series2.data.setIndex(2, {
      category: 'C range',
      value2: this.displayData.c_range.num_failed,
      pass_rate: this.displayData.c_range.fail_rate,
      course_code: this.displayData.course_code,
    });
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  exportChart() {
    this.dataService.exportAmChartsAsPNG(
      this.root,
      `prerequisite-chart-${this.targetCrs.course_code}`,
    );
  }
}
